/**
 * Apply sigmoid mask to original image → transparent PNG
 * mask: Float32Array [1, 1, 1024, 1024] flattened → values 0-1
 * Returns canvas with transparency applied
 */
export function applyMask(
	original: ImageData,
	mask: Float32Array,
	_origWidth: number,
	_origHeight: number,
	srcCanvas: HTMLCanvasElement,
): HTMLCanvasElement {
	const { width, height } = original;
	const result = document.createElement("canvas");
	result.width = width;
	result.height = height;
	const ctx = result.getContext("2d")!;

	// Draw original
	ctx.drawImage(srcCanvas, 0, 0);
	const outputData = ctx.getImageData(0, 0, width, height);

	// The mask is 1024x1024 — needs to be resized to original dimensions
	const maskSize = Math.round(Math.sqrt(mask.length));

	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			// Bilinear sample from mask
			const mx = (x / width) * (maskSize - 1);
			const my = (y / height) * (maskSize - 1);
			const alpha = bilinearSample(mask, maskSize, mx, my);

			const pixelIdx = (y * width + x) * 4;
			outputData.data[pixelIdx + 3] = Math.round(alpha * 255);
		}
	}

	ctx.putImageData(outputData, 0, 0);
	return result;
}

function bilinearSample(
	data: Float32Array,
	size: number,
	x: number,
	y: number,
): number {
	const x0 = Math.floor(x);
	const y0 = Math.floor(y);
	const x1 = Math.min(x0 + 1, size - 1);
	const y1 = Math.min(y0 + 1, size - 1);

	const fx = x - x0;
	const fy = y - y0;

	const v00 = data[y0 * size + x0];
	const v10 = data[y0 * size + x1];
	const v01 = data[y1 * size + x0];
	const v11 = data[y1 * size + x1];

	return (
		v00 * (1 - fx) * (1 - fy) +
		v10 * fx * (1 - fy) +
		v01 * (1 - fx) * fy +
		v11 * fx * fy
	);
}
